import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { PLATFORM_ID, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataService, PamlabApiServiceService } from '@/shared/services';
import { MatOption } from '@angular/material/core';
import {
  StudentFinancialAidDataReturn,
  FinancialAidTableData,
} from '../../../../server/types';
import { isPlatformBrowser } from '@angular/common';
import { Papa } from 'ngx-papaparse';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { RouteService } from '@/shared/services';
import { HttpErrorResponse } from '@angular/common/http';

interface FinancialAidForm {
  aid_years: number[];
  student_types: string[];
  fund_sources: string[];
  fund_types: string[];
}

interface FundType {
  code: string;
  desc: string;
  source: string;
}

@Component({
  selector: 'financial-aid-form',
  templateUrl: './financial-aid-form.component.html',
  styleUrls: ['./financial-aid-form.component.css'],
  standalone: false,
})
export class FinancialAidFormComponent implements OnInit, OnDestroy {
  @ViewChild('allSelectedYears') private allSelectedYears!: MatOption;
  @ViewChild('allSelectedStudentGroup')
  private allSelectedStudentGroup!: MatOption;
  @ViewChild('allSelectedFundSource') private allSelectedFundSource!: MatOption;
  @ViewChild('allSelectedFundType') private allSelectedFundType!: MatOption;

  //variable to keep track of spinner's status
  public spinnerBool: boolean = true;

  // to save fund type to display in form field Fund Type
  fundTypeOption: FundType[] = [];

  public clickedAllYears: boolean = false;
  public clickedAllStudentGroup: boolean = false;
  public clickedAllFundSource: boolean = false;
  public clickedAllFundType: boolean = false;

  public isYearsEmpty: boolean = true;
  public isStudentGroupEmpty: boolean = true;
  public isFundSourceEmpty: boolean = true;
  public isFundTypeEmpty: boolean = true;

  // variables to generate list of values for dropdown
  yearList: string[] = [
    '2017-2018',
    '2018-2019',
    '2019-2020',
    '2020-2021',
    '2021-2022',
  ];
  studentGroupList: string[] = ['FTIC', 'Transfer', 'Continuing'];
  fundSourceList: string[] = ['Institutional', 'Federal', 'State'];

  private tableData: FinancialAidTableData[] = [];

  // cohort form group
  public financialAidForm = new FormGroup({
    aid_years: new FormControl(),
    student_types: new FormControl(),
    fund_sources: new FormControl(),
    fund_types: new FormControl(),
  });

  public funds = {
    continuing: {
      institutional: [
        {
          code: 'ACEX',
          desc: 'Academic Excellence',
          source: 'institutional',
        },
        {
          code: 'NAUT',
          desc: 'Nautilus',
          source: 'institutional',
        },
        {
          code: 'FAGR',
          desc: 'Financial Aid Grant',
          source: 'institutional',
        },
        {
          code: 'EHAN',
          desc: 'Enhancement Grant',
          source: 'institutional',
        },
        {
          code: 'FAOS',
          desc: 'Financial Aid Scholarship',
          source: 'institutional',
        },
        {
          code: 'ISGF',
          desc: 'Institutional Assistance Grant',
          source: 'institutional',
        },
      ],
      federal: [
        {
          code: 'PELL',
          desc: 'Pell',
          source: 'federal',
        },
        {
          code: 'SEOG',
          desc: 'Supplement Education Opportunity',
          source: 'federal',
        },
      ],
      state: [
        {
          code: 'FSAG',
          desc: 'Florida Student Assistance Grant',
          source: 'state',
        },
        {
          code: 'FGMG',
          desc: 'First Generation Matching Grant',
          source: 'state',
        },
      ],
    },
    ftic: {
      institutional: [
        {
          code: 'ACEF',
          desc: 'Academic Excellence',
          source: 'institutional',
        },
        {
          code: 'NAUF',
          desc: 'Nautilus',
          source: 'institutional',
        },
        {
          code: 'FAGF',
          desc: 'Financial Aid Grant',
          source: 'institutional',
        },
        {
          code: 'EHNF',
          desc: 'Enhancement Grant',
          source: 'institutional',
        },
        {
          code: 'GAP',
          desc: 'Academic Excellence',
          source: 'institutional',
        },
      ],
      federal: [
        {
          code: 'SEOF',
          desc: 'Supplement Education Opportunity',
          source: 'federal',
        },
        {
          code: 'PELL',
          desc: 'Pell',
          source: 'federal',
        },
      ],
      state: [
        {
          code: 'FGMF',
          desc: 'First Generation Matching Grant',
          source: 'state',
        },
        {
          code: 'FSGF',
          desc: 'Florida Assistance Grant',
          source: 'state',
        },
      ],
    },
    transfer: {
      institutional: [
        {
          code: 'NATR',
          desc: 'Nautilus',
          source: 'institutional',
        },
        {
          code: 'ARG',
          desc: 'Argonaut',
          source: 'institutional',
        },
        {
          code: 'GAP',
          desc: 'Institutional Supplemental Grant',
          source: 'institutional',
        },
        {
          code: 'FAGT',
          desc: 'Financial Aid Grant',
          source: 'institutional',
        },
        {
          code: 'EHNT',
          desc: 'Enhancement Grant',
          source: 'institutional',
        },
        {
          code: 'TSGR',
          desc: 'Transfer Grant',
          source: 'institutional',
        },
      ],
      federal: [
        {
          code: 'PELL',
          desc: 'Pell',
          source: 'federal',
        },
        {
          code: 'SEOT',
          desc: 'Supplemental Education Opportunity',
          source: 'federal',
        },
      ],
      state: [
        {
          code: 'FSGT',
          desc: 'Florida Student Assistance Grant',
          source: 'state',
        },
      ],
    },
  };

  // form variable to check for previous form submission
  prevParam: FinancialAidForm = {
    aid_years: [],
    student_types: [],
    fund_sources: [],
    fund_types: [],
  };

  // variables to keep track of chosen values by user to get data
  chosenYears: string[] = [];
  chosenStudentTypes: string[] = [];
  chosenFundTypes: string[] = [];
  chosenFundSources: string[] = [];

  // boolean variables for event variable in function
  isYearClose!: boolean;
  isStudentGroupClose!: boolean;
  isFundSourceClose!: boolean;
  isFundTypeClose!: boolean;
  showExportBtn: boolean = false;

  // to save form values for values selected
  yearChosen = new FormControl();
  studentGroupChosen = new FormControl();
  fundSourceChosen = new FormControl();
  fundTypeChosen = new FormControl();

  private subscription = new Subscription();

  constructor(
    private _dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _papa: Papa,
    private _routeService: RouteService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  financialAidFormSubmit() {
    this._dataService.setPendingData(true);
    // saving chosen values to cohortForm for submission
    this.financialAidForm.value.aid_years = this.chosenYears.map(
      (year) => +year.replace('-', ''),
    );
    this.financialAidForm.value.student_types = this.chosenStudentTypes;
    this.financialAidForm.value.fund_sources = this.chosenFundSources;
    this.financialAidForm.value.fund_types = this.chosenFundTypes;

    this.subscription.add(
      this._apiService
        .getFinancialAidData({
          aid_years: this.financialAidForm.value.aid_years,
          fund_sources: this.financialAidForm.value.fund_sources,
          fund_types: this.financialAidForm.value.fund_types,
          student_types: this.financialAidForm.value.student_types,
        })
        .subscribe({
          next: (data: StudentFinancialAidDataReturn) => {
            this._dataService.setData({ data });
            this.tableData = data.table_data;
            this.showExportBtn = true;
            this._dataService.setPendingData(false);
          },
          error: (errorRes: HttpErrorResponse) => {
            if (errorRes.status === 401) {
              this._routeService.navigate(environment['login_url']);
            }
            this._dataService.setData({ error: errorRes.error });
            this._dataService.setPendingData(false);
          },
        }),
    );
  }

  // dropdown year input to detect when it is open and close to save value selected
  yearMultiSelectChange(event: any) {
    // if dropdown is closed save value to chosenYears variable
    if (!event) {
      this.isYearClose = true;

      // if all years is not selected
      if (!this.allSelectedYears.selected) {
        this.chosenYears = this.yearChosen.value;

        // if user selected values for student group set isYearsEmpty = false
        if (this.chosenYears) {
          this.isYearsEmpty = false;
        }
      }
    } else {
      this.isYearClose = false;
      this.isYearsEmpty = true;
    }
  }

  // dropdown student group input to detect when it is open and close to save value selected
  studentGroupMultiSelectChange(event: any) {
    // if dropdown is closed save value to chosenStudentTypes variable
    if (!event) {
      this.isStudentGroupClose = true;

      // if all student group is not selected
      if (!this.allSelectedStudentGroup.selected) {
        this.chosenStudentTypes = this.studentGroupChosen.value;

        // if user selected values for student group set isStudentGroupEmpty = false
        if (this.chosenStudentTypes) {
          this.isStudentGroupEmpty = false;
        }
      }
    } else {
      this.isStudentGroupClose = false;
      this.isStudentGroupEmpty = true;
    }
  }

  // dropdown fund source to detect when it is open and close to save value selected
  fundSourceMultiSelectChange(event: any) {
    // if dropdown is closed save value to chosenFundSources variable
    if (!event) {
      this.isFundSourceClose = true;

      // if all fund source is not selected
      if (!this.allSelectedFundSource.selected) {
        this.chosenFundSources = this.fundSourceChosen.value;

        // if user selected values for student group set isFundSourceEmpty = false
        if (this.chosenFundSources) {
          this.isFundSourceEmpty = false;
        }
      }
    } else {
      this.isFundSourceClose = false;
      this.isFundSourceEmpty = true;
    }

    this.chosenFinancialAidFilter(
      this.chosenStudentTypes,
      this.chosenFundSources,
    );
  }

  // dropdown fund type input to detect when it is open and close to save value selected
  fundTypeMultiSelectChange(event: any) {
    // if dropdown is closed save value to chosenFundTypes variable
    if (!event) {
      this.isFundTypeClose = true;

      // if all fund type is not selected
      if (!this.allSelectedFundType.selected) {
        this.chosenFundTypes = this.fundTypeChosen.value;

        // if user selected values for student group set isFundTypeEmpty = false
        if (this.chosenFundTypes) {
          this.isFundTypeEmpty = false;
        }
      }
    } else {
      this.isFundTypeClose = false;
      this.isFundTypeEmpty = true;
    }
  }

  // toggle multi select dropdown if user click on 'All Years'
  toggleAllYearsSelection() {
    if (this.allSelectedYears.selected) {
      this.financialAidForm.controls.aid_years.patchValue([
        ...this.yearList.map((item) => item),
      ]);
      this.chosenYears = this.financialAidForm.value.aid_years;
    } else {
      this.financialAidForm.controls.aid_years.patchValue([]);
    }
  }

  // toggle multi select dropdown if user click on 'All Student Group'
  toggleAllStudentGroupSelection() {
    if (this.allSelectedStudentGroup.selected) {
      this.financialAidForm.controls.student_types.patchValue([
        ...this.studentGroupList.map((item) => item),
      ]);
      this.chosenStudentTypes = ['All'];
    } else {
      this.financialAidForm.controls.student_types.patchValue([]);
    }
  }

  // toggle multi select dropdown if user click on 'All Fund Source'
  toggleAllFundSourceSelection() {
    if (this.allSelectedFundSource.selected) {
      this.financialAidForm.controls.fund_sources.patchValue([
        ...this.fundSourceList.map((item) => item),
      ]);
      this.chosenFundSources = ['All'];
    } else {
      this.financialAidForm.controls.fund_sources.patchValue([]);
    }
  }

  // toggle multi select dropdown if user click on 'All Fund Type'
  toggleAllFundTypeSelection() {
    if (this.allSelectedFundType.selected) {
      this.financialAidForm.controls.fund_types.patchValue([
        ...this.fundTypeOption.map((item) => item.code),
      ]);
      this.chosenFundTypes = ['All'];
    } else {
      this.financialAidForm.controls.fund_types.patchValue([]);
    }
  }

  // if all years
  allYearsClicked() {
    this.clickedAllYears = !this.clickedAllYears;
    this.isYearsEmpty = false;
  }

  // if all student group
  allStudentGroupClicked() {
    this.clickedAllStudentGroup = !this.clickedAllStudentGroup;
    this.isStudentGroupEmpty = false;
  }

  // if all fund source
  allFundSourceClicked() {
    this.clickedAllFundSource = !this.clickedAllFundSource;
    this.isFundSourceEmpty = false;
  }

  // if all fund type
  allFundTypeClicked() {
    this.clickedAllFundType = !this.clickedAllFundType;
    this.isFundTypeEmpty = false;
  }

  // To determine what college user choose to change program option for form field
  chosenFinancialAidFilter(studentGroup: any, fundSource: any) {
    if (!studentGroup || !fundSource) return;

    this.fundTypeOption = [];

    // if "All" is in studetGroup and fundSource then assign those value
    if (studentGroup.includes('All')) studentGroup = 'Continuing,FTIC,Transfer';
    if (fundSource.includes('All')) fundSource = 'Institutional,Federal,State';

    //  FTIC, All
    if (studentGroup.includes('FTIC') && fundSource === 'All') {
      this.fundTypeOption = [
        ...this.funds.ftic.institutional,
        ...this.funds.ftic.federal,
        ...this.funds.ftic.state,
      ];
    }

    //  FTIC, Institutional
    if (studentGroup.includes('FTIC') && fundSource.includes('Institutional')) {
      this.fundTypeOption.push(...this.funds.ftic.institutional);
    }

    //  FTIC, Federal
    if (studentGroup.includes('FTIC') && fundSource.includes('Federal')) {
      this.fundTypeOption.push(...this.funds.ftic.federal);
    }

    //  FTIC, State
    if (studentGroup.includes('FTIC') && fundSource.includes('State')) {
      this.fundTypeOption.push(...this.funds.ftic.state);
    }

    //  Transfer, All
    if (studentGroup.includes('Transfer') && fundSource === 'All') {
      this.fundTypeOption = [
        ...this.funds.transfer.institutional,
        ...this.funds.transfer.federal,
        ...this.funds.transfer.state,
      ];
    }

    //  Transfer, Institutional
    if (
      studentGroup.includes('Transfer') &&
      fundSource.includes('Institutional')
    ) {
      this.fundTypeOption.push(...this.funds.transfer.institutional);
    }

    //  Transfer, Federal
    if (studentGroup.includes('Transfer') && fundSource.includes('Federal')) {
      this.fundTypeOption.push(...this.funds.transfer.federal);
    }

    //  Transfer, State
    if (studentGroup.includes('Transfer') && fundSource.includes('State')) {
      this.fundTypeOption.push(...this.funds.transfer.state);
    }

    //  Continuing, All
    if (studentGroup.includes('Continuing') && fundSource === 'All') {
      this.fundTypeOption = [
        ...this.funds.continuing.institutional,
        ...this.funds.continuing.federal,
        ...this.funds.continuing.state,
      ];
    }

    //  Continuing, Institutional
    if (
      studentGroup.includes('Continuing') &&
      fundSource.includes('Institutional')
    ) {
      this.fundTypeOption.push(...this.funds.continuing.institutional);
    }

    //  Continuing, Federal
    if (studentGroup.includes('Continuing') && fundSource.includes('Federal')) {
      this.fundTypeOption.push(...this.funds.continuing.federal);
    }

    //  Continuing, State
    if (studentGroup.includes('Continuing') && fundSource.includes('State')) {
      this.fundTypeOption.push(...this.funds.continuing.state);
    }

    this.fundTypeOption = [...new Set(this.fundTypeOption)];
  }

  // close popup _dialog after form is submitted
  closeDialog() {
    this._dialog.closeAll();
  }

  exportData() {
    if (isPlatformBrowser(this.platformId)) {
      const csvData = this._papa.unparse(this.tableData as any);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const pom = document.createElement('a');
      pom.href = url;
      pom.setAttribute('download', 'export.csv');
      pom.click();
    }
  }

  // Error message when user leaves form fields empty
  getErrorMessage() {
    return 'You must enter a value';
  }

  // function to check if isStudentGroupEmpty, isFundSourceEmpty, isFundTypeEmpty, and isYearsEmpty is fully filled
  // if it is submit button will be enabled for submission
  disabledSubmitButton() {
    if (
      !this.isStudentGroupEmpty &&
      !this.isFundSourceEmpty &&
      !this.isFundTypeEmpty &&
      !this.isYearsEmpty
    ) {
      return false;
    } else {
      return true;
    }
  }
}
